import React from 'react';
import clsx from 'clsx';
import { SecondaryButton } from '@/components/buttons/SecondaryButton/SecondaryButton';
import PlusSign from '@/assets/icons/icon-plus-sign.svg?react';
import { ImagePreview } from '../ImagePreview/ImagePreview';
import { SelectField } from '@/components/dropdowns/SelectField/SelectField';
import { SelectItem } from '@/components/ui/select';
import { SliderField } from '../SliderField/SliderField';
import type { BackgroundImageMode, BackgroundPosition } from '@builder4/shared/types/layout';
import {
  type ImageContinuousType,
  IMAGE_CONTINUOUS_TYPES,
  IMAGE_CONTINUOUS_LABELS,
  CONTINUOUS_DEFAULTS,
  CONTINUOUS_SPEED_CONFIG,
  CONTINUOUS_INTENSITY_CONFIG,
} from '@builder4/shared/types/animation';
import { useImageManager } from '@/hooks/useImageManager';
import type { AssetResponse } from '@lp/lib-asset-api';

interface BackgroundImageProps {
  className: string;
  onImageSelected: (bgImageUrl: string | null, bgImageId?: string) => void;
  selectedImage?: string | null;
  selectedImageId?: string | null;
  scale?: number;
  onScaleChange: (scale: number) => void;
  opacity?: number;
  onOpacityChange: (opacity: number) => void;
  mode?: BackgroundImageMode;
  position?: BackgroundPosition;
  onModeChange: (mode: BackgroundImageMode) => void;
  onPositionChange: (position: BackgroundPosition) => void;
  continuousAnimation?: ImageContinuousType;
  continuousSpeed?: number;
  continuousIntensity?: number;
  onContinuousAnimationChange?: (type: ImageContinuousType) => void;
  onContinuousSpeedChange?: (speed: number) => void;
  onContinuousIntensityChange?: (intensity: number) => void;
}

export const BackgroundImage: React.FC<BackgroundImageProps> = ({
  className = '',
  onImageSelected,
  selectedImage,
  selectedImageId,
  onScaleChange,
  scale = 100,
  opacity = 100,
  onOpacityChange,
  mode = 'fill',
  onModeChange,
  position = 'center center',
  onPositionChange,
  continuousAnimation = 'none',
  continuousSpeed,
  continuousIntensity,
  onContinuousAnimationChange,
  onContinuousSpeedChange,
  onContinuousIntensityChange,
}) => {
  const { openImageManager } = useImageManager();

  const handleSelectImage = (image: AssetResponse) => {
    onImageSelected?.(image.fileUri, image._meta.id);
  };

  const handleAddClick = () => {
    openImageManager({ onSelectImage: handleSelectImage });
  };

  const handleReplaceClick = () => {
    openImageManager({
      onSelectImage: handleSelectImage,
      activeImageId: selectedImageId ?? undefined,
    });
  };

  const handleRemoveClick = () => {
    onImageSelected?.(null);
  };

  return (
    <div className={clsx('', className)}>
      {!selectedImage && (
        <div className="flex flex-col gap-3">
          <p className="justify-start pb-1 font-['Inter'] text-xs font-semibold text-neutral-900">
            Image
          </p>

          <div className="flex w-full items-center justify-between gap-2 py-0.5">
            <p className="text-button-sm justify-start font-medium text-neutral-500">
              Background Image
            </p>

            <SecondaryButton
              icon={PlusSign}
              size="sm"
              state="default"
              text="Add"
              onClick={handleAddClick}
            />
          </div>
        </div>
      )}

      {selectedImage && (
        <div className="flex flex-col gap-3">
          <ImagePreview
            image={selectedImage}
            onReplaceClick={handleReplaceClick}
            onRemoveClick={handleRemoveClick}
            opacity={opacity}
            mode={mode}
            position={position}
            scale={scale}
          />

          <SelectField
            className="max-w-[216px]"
            placeholder="Mode"
            size="small"
            text="Mode"
            defaultValue={mode}
            dataTestId="bg-image-mode-select"
            onValueChange={(value) => onModeChange(value as BackgroundImageMode)}
          >
            <SelectItem value="fill">Fill</SelectItem>
            <SelectItem value="fit">Fit</SelectItem>
            <SelectItem value="stretch">Stretch</SelectItem>
            <SelectItem value="tile">Tile</SelectItem>
          </SelectField>

          <SelectField
            className="max-w-[216px]"
            placeholder="Position"
            text="Position"
            size="small"
            defaultValue={position}
            dataTestId="bg-image-position-select"
            onValueChange={(value) => onPositionChange(value as BackgroundPosition)}
          >
            <SelectItem value="left top">Top Left</SelectItem>
            <SelectItem value="center top">Top Center</SelectItem>
            <SelectItem value="right top">Top Right</SelectItem>
            <SelectItem value="left center">Left</SelectItem>
            <SelectItem value="center center">Center</SelectItem>
            <SelectItem value="right center">Right</SelectItem>
            <SelectItem value="left bottom">Bottom Left</SelectItem>
            <SelectItem value="center bottom">Bottom Center</SelectItem>
            <SelectItem value="right bottom">Bottom Right</SelectItem>
          </SelectField>

          {mode === 'tile' && (
            <SliderField
              className="w-full max-w-[216px]"
              label="Scale"
              suffix="%"
              max={300}
              min={0}
              onChange={() => {}}
              onChangeComplete={(value) => onScaleChange(value)}
              value={scale}
            />
          )}
          <SliderField
            className="w-full max-w-[216px]"
            label="Opacity"
            max={100}
            min={0}
            onChange={() => {}}
            onChangeComplete={(value) => onOpacityChange(value)}
            value={opacity}
          />

          {onContinuousAnimationChange && (
            <>
              <SelectField
                className="max-w-[216px]"
                placeholder="Animation"
                text="Animation"
                size="small"
                defaultValue={continuousAnimation}
                dataTestId="bg-continuous-animation-select"
                onValueChange={(value) => onContinuousAnimationChange(value as ImageContinuousType)}
              >
                {IMAGE_CONTINUOUS_TYPES.map((type) => (
                  <SelectItem key={type} value={type}>
                    {IMAGE_CONTINUOUS_LABELS[type]}
                  </SelectItem>
                ))}
              </SelectField>

              {continuousAnimation !== 'none' &&
                (() => {
                  const contType = continuousAnimation as Exclude<ImageContinuousType, 'none'>;
                  const speedConfig = CONTINUOUS_SPEED_CONFIG[contType];
                  const intensityConfig = CONTINUOUS_INTENSITY_CONFIG[contType];
                  const defaults = CONTINUOUS_DEFAULTS[contType];
                  return (
                    <>
                      <SliderField
                        className="w-full max-w-[216px]"
                        label="Speed"
                        value={continuousSpeed ?? defaults.speed}
                        onChange={() => {}}
                        onChangeComplete={(value) => onContinuousSpeedChange?.(value)}
                        min={speedConfig.min}
                        max={speedConfig.max}
                        step={speedConfig.step}
                        suffix="s"
                      />
                      <SliderField
                        className="w-full max-w-[216px]"
                        label={intensityConfig.label}
                        value={continuousIntensity ?? defaults.intensity}
                        onChange={() => {}}
                        onChangeComplete={(value) => onContinuousIntensityChange?.(value)}
                        min={intensityConfig.min}
                        max={intensityConfig.max}
                        step={intensityConfig.step}
                        suffix={intensityConfig.suffix}
                      />
                    </>
                  );
                })()}
            </>
          )}
        </div>
      )}
    </div>
  );
};
